import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";

import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

const Title = styled.h3`
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
`;

const Subtitle = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;
`;

const Hint = styled.div`
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 8px;
`;

const SectionLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #374151;
    margin: 14px 0 6px 0;

    &:first-of-type {
        margin-top: 0;
    }
`;

const TableWrap = styled.div`
    max-height: ${(p) => (p.$compact ? "min(32vh, 260px)" : "min(48vh, 420px)")};
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
    }
    th, td {
        padding: 6px 8px;
        text-align: left;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
    }
    th {
        background: #f8fafc;
        position: sticky;
        top: 0;
        z-index: 1;
    }
    tr.dirty td {
        background: #fef3c7;
    }
    tr.saved td {
        background: #ecfdf5;
    }
    .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
    .empty {
        padding: 14px;
        color: #6b7280;
        text-align: center;
    }

    input[type="number"] {
        width: 90px;
        padding: 4px 6px;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        font-size: 12px;
        text-align: right;
        appearance: textfield;
        -moz-appearance: textfield;
    }
    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input[type="number"]:focus {
        outline: 2px solid #93c5fd;
        outline-offset: 1px;
    }
`;

const RowActionBtn = styled.button`
    border: 1px solid #d1d5db;
    background: #f8fafc;
    color: #1f2937;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #e2e8f0;
        border-color: #94a3b8;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 10px;
    font-size: 11px;
    color: #6b7280;
`;

/**
 * Lista os pares (UF, município) dos colaboradores ativos do TOTVS para a obra,
 * já cruzando com os registros existentes em obra_municipio_km. Permite preencher
 * a distância em cada linha e salvar individualmente.
 *
 * Overlay não fecha ao clicar — fecha só pelo X.
 */
const ModalCadastroDistancias = ({ obra }) => {

    const api = useApi();
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    /** id do registro sendo salvo agora (chave: row.key). */
    const [savingKey, setSavingKey] = useState(null);

    const buildKey = (row) => `${row.estado}|${row.municipio}`;

    const fetchList = async () => {
        if (!obra?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/Painel/API/FolgasCampo/Distancias/ListColaboradores/index.php?centro_custo_folga_id=${obra.id}`
            );
            const rows = (data?.success && Array.isArray(data.items) ? data.items : []).map((r) => ({
                ...r,
                /** valor controlado pelo input; começa com o que está no banco (ou string vazia). */
                inputValue: r.distancia != null ? String(r.distancia) : "",
                lastSaved: r.distancia != null ? String(r.distancia) : "",
                justSaved: false,
            }));
            setItems(rows);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Falha ao carregar colaboradores do centro de custo.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obra?.id]);

    const handleChange = (key, newValue) => {
        setItems((prev) =>
            prev.map((row) =>
                buildKey(row) === key ? { ...row, inputValue: newValue, justSaved: false } : row
            )
        );
    };

    const handleSaveRow = async (row) => {
        if (!obra?.id) return;
        const key = buildKey(row);
        const trimmed = (row.inputValue ?? "").trim();
        if (trimmed === "") {
            notifyError("Preencha a distância antes de salvar.");
            return;
        }
        const km = parseInt(trimmed, 10);
        if (!Number.isFinite(km) || km < 0) {
            notifyError("Distância inválida.");
            return;
        }

        setSavingKey(key);
        try {
            const { data } = await api.post("/Painel/API/FolgasCampo/Distancias/Save/index.php", {
                id: row.obra_municipio_km_id || undefined,
                centro_custo_folga_id: obra.id,
                estado: row.estado,
                municipio: row.municipio,
                distancia: km,
            });

            const savedId = data?.id || row.obra_municipio_km_id || null;
            setItems((prev) =>
                prev.map((r) =>
                    buildKey(r) === key
                        ? {
                              ...r,
                              distancia: km,
                              lastSaved: String(km),
                              obra_municipio_km_id: savedId,
                              justSaved: true,
                          }
                        : r
                )
            );
            notifySuccess(row.obra_municipio_km_id ? "Distância atualizada." : "Distância cadastrada.");
        } catch (err) {
            notifyError(err?.response?.data?.message || "Erro ao salvar distância.");
        } finally {
            setSavingKey(null);
        }
    };

    const { semDistanciaCadastrada, comDistanciaCadastrada } = useMemo(() => {
        const cmpUfMun = (a, b) =>
            `${a.estado}|${a.municipio}`.localeCompare(`${b.estado}|${b.municipio}`, "pt-BR");
        const sem = [];
        const com = [];
        for (const row of items) {
            if (row.distancia == null) sem.push(row);
            else com.push(row);
        }
        sem.sort(cmpUfMun);
        com.sort(cmpUfMun);
        return { semDistanciaCadastrada: sem, comDistanciaCadastrada: com };
    }, [items]);

    if (!obra) return null;

    const total = items.length;
    const totalPreenchidos = items.filter((r) => r.distancia != null).length;

    const renderTableRows = (list) =>
        list.map((row) => {
            const key = buildKey(row);
            const dirty = (row.inputValue || "").trim() !== (row.lastSaved || "");
            const rowClass = row.justSaved ? "saved" : dirty ? "dirty" : "";
            return (
                <tr key={key} className={rowClass}>
                    <td>{row.estado}</td>
                    <td>{row.municipio}</td>
                    <td className="num">{row.qtd_colaboradores}</td>
                    <td className="num">
                        <input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={row.inputValue}
                            onChange={(e) => handleChange(key, e.target.value)}
                        />
                    </td>
                    <td className="num">
                        <RowActionBtn
                            type="button"
                            disabled={!dirty || savingKey === key}
                            onClick={() => handleSaveRow(row)}
                        >
                            {savingKey === key ? "…" : "SALVAR"}
                        </RowActionBtn>
                    </td>
                </tr>
            );
        });

    const tableHead = (
        <thead>
            <tr>
                <th>UF</th>
                <th>MUNICÍPIO</th>
                <th className="num">QTD COLAB.</th>
                <th className="num">DISTÂNCIA (KM)</th>
                <th className="num">AÇÃO</th>
            </tr>
        </thead>
    );

    return (
        <Overlay state>
            <Modal size="lg" header close={closeModal}>
                <Title>DISTÂNCIAS — {obra.obra}</Title>
                <Subtitle>Prazo do ciclo: <strong>{obra.dias_prazo}</strong> dias</Subtitle>
                <Hint>
                    Lista de UF/município dos colaboradores ATIVOS do centro de custo.
                    Pendentes ficam na tabela menor no topo; após cadastrar, a linha passa para
                    “Já cadastradas”.
                </Hint>

                {loading ? (
                    <TableWrap>
                        <div className="empty">Carregando colaboradores…</div>
                    </TableWrap>
                ) : items.length === 0 ? (
                    <TableWrap>
                        <div className="empty">Nenhum colaborador ativo encontrado nesse centro de custo.</div>
                    </TableWrap>
                ) : (
                    <>
                        <SectionLabel>A preencher (sem distância no banco) — {semDistanciaCadastrada.length}</SectionLabel>
                        <TableWrap $compact>
                            {semDistanciaCadastrada.length === 0 ? (
                                <div className="empty">Nenhuma UF/município pendente.</div>
                            ) : (
                                <table>
                                    {tableHead}
                                    <tbody>{renderTableRows(semDistanciaCadastrada)}</tbody>
                                </table>
                            )}
                        </TableWrap>

                        <SectionLabel>Já cadastradas — {comDistanciaCadastrada.length}</SectionLabel>
                        <TableWrap>
                            {comDistanciaCadastrada.length === 0 ? (
                                <div className="empty">Nenhuma distância cadastrada ainda.</div>
                            ) : (
                                <table>
                                    {tableHead}
                                    <tbody>{renderTableRows(comDistanciaCadastrada)}</tbody>
                                </table>
                            )}
                        </TableWrap>
                    </>
                )}

                <Footer>
                    <span>
                        {totalPreenchidos} de {total} preenchidos
                    </span>
                </Footer>
            </Modal>
        </Overlay>
    );
};

export default ModalCadastroDistancias;
